import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import path from 'path';
import Cropper from 'cropperjs';
import { guid } from '@/mixins/guid';
import { uploader } from '@/mixins/aws/uploader/s3';
import uploadProgress from '@/mixins/aws/uploader';
import tooltip from '@/mixins/components/tooltip';

export default Vue.component('dynamic-input-gallery', {
  template: require('./template.html'),
  name: 'dynamic-input-gallery',
  mixins: [guid, uploader],
  computed: {
    ...mapGetters(['currentSite', 'activeSiteType']),
  },
  components: {
    uploadProgress,
    tooltip,
  },
  props: [
    'active-item',
    'type',
    'active-input',
    'value',
    'inner-tabs',
    'dynamic-tab',
    'open-cropper',
  ],
  data() {
    return {
      strictId: null,
      confirmDeleteImg: false,
      showAll: false,
      paginate: [this.type],
      loading: false,
      deleting: false,
      activeImage: null,
      activeDelete: null,
      shownLimit: 5,
      itemLimit: 5,
      cropper: null,
      localFile: null,
      targetFile: null,
      targetName: '',
      activeAspect: {},
      restrictAspect: false,
      aspectRatios: {
        raw: {
          display: 'Raw Image (Ignore Cropper)',
          ratio: 0,
        },
        free: {
          display: 'Free',
        },
        shortRect: {
          ratio: 4 / 5,
          height: 750,
          display: '4:5',
          width: 600,
        },
        tallRect: {
          ratio: 2 / 3,
          height: 750,
          display: '2:3',
          width: 500,
        },
        square: {
          ratio: 1 / 1,
          height: 600,
          display: '1:1',
          width: 600,
        },
        smallWideRect: {
          ratio: 4 / 3,
          display: '4:3',
          height: 600,
          width: 800,
        },
        wideRect: {
          ratio: 16 / 9,
          height: 700,
          display: '16:9',
          width: 1244,
        },
      },
    };
  },
  watch: {
    innerTabs: {
      handler(tab) {
        if (tab[this.dynamicTab]) {
          this.setInitialValues();
        }
        if (this.value.dimensions) {
          // always destroy cropper when tabs are changed
          this.destroyCropper();
        }
      },
      deep: true,
    },
    openCropper(open) {
      if (open) {
        setTimeout(() => this.setCropper(), 300);
      }
    },
    showAll(shown) {
      if (shown) {
        this.itemLimit = this.activeItem[this.type].length;
      } else {
        this.itemLimit = this.shownLimit;
      }
    },
  },
  created() {
    this.setInitialValues();
    this.strictId = this.guid();
  },
  methods: {
    setInitialValues() {
      // for s3 assets
      this.bucketPath = `_droplet-assets/${this.activeSiteType}/${this.type}/`;
      this.prefix = `drz_${this.activeSiteType}_${Date.now()}_`;
      this.thumbnails = `_droplet-assets/${this.activeSiteType}/${this.type}/_thumbnails/`;
      this.activeAspect = this.value.dimensions;
      this.restrictAspect = this.activeAspect.restrict;
    },
    setCropper() {
      if (this.activeAspect.ratio !== 0) {
        const image = this.$refs.imageCropper;
        this.cropper = new Cropper(image, {
          aspectRatio: this.activeAspect.ratio,
          dragMode: 'move',
          autoCropArea: 1,
          zoomable: false,
        });
      }
    },
    setAspect(aspect) {
      this.activeAspect = aspect;
      if (this.cropper) {
        this.cropper.destroy();
      }
      this.cropper = null;
      this.setCropper();
    },
    getThumbnail(image) {
      let thumb = this._getThumbFromFile(image.src);
      if (image.src.startsWith('/')) {
        thumb = `${this._servePrefix(image.src)}/${this.thumbnails}${thumb}`;
      }
      return `url('${thumb}')`;
    },
    openUpload() {
      this.$refs.uploadFile.click();
    },
    openLocalImg(e) {
      this.$validator.validateAll().then(async (result) => {
        // run only if file types and dimensions are correct
        if (result) {
          const evt = e;
          let newFile = [...evt.target.files];
          if (newFile.length > 0) {
            try {
              newFile = newFile[0];
              this.targetFile = newFile;
              let targetName = path.basename(newFile.name);
              targetName = `${this.prefix}${_.trim(targetName)}`;
              this.targetName = _.replace(targetName, /\s+/g, '_');
              const base64File = await this.fileToBase64(newFile);
              const img = new Image();
              img.src = base64File;
              await new Promise((r) => { img.onload = () => r(); });
              this.$emit('set-cropper', base64File, this.type);
              // clean up the file name for s3
              let fileName = this.replaceExt({ file: path.basename(newFile.name), ext: '.png' });
              fileName = `${this.prefix}${_.trim(fileName)}`;
              this.localFile = _.replace(fileName, /\s+/g, '_');
            } catch (err) {
              this.$log.error(err);
              await this._reportError({
                error: err,
                fn: 'item openLocalImg',
              });
            }
          }
          // reset value to null so we can get the "change" event to run
          // in any case. Even when the user selects the same file
          evt.target.value = null;
        }
      });
    },
    destroyCropper() {
      this.$emit('set-cropper', false, false);
      this.localFile = null;
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
    },
    getBlob(canvas) {
      return new Promise((resolve) => {
        canvas.toBlob(blob => resolve(blob));
      });
    },
    async uploadCropped() {
      try {
        let file = this.targetFile;
        let name = this.targetName;
        if (this.activeAspect.ratio !== 0) {
          let { width, height } = this.cropper.getContainerData();
          if (this.activeAspect.width) {
            width = this.activeAspect.width;
          }

          if (this.activeAspect.height) {
            height = this.activeAspect.height;
          }
          const canvas = this.cropper.getCroppedCanvas({ width, height, imageSmoothingQuality: 'high' });
          file = await this.getBlob(canvas);
          name = this.localFile;
        }
        const filePath = `/${this.bucketPath}${name}`;
        await this.uploadFile({
          file: `${this._sitePath}${filePath}`,
          body: file,
          thumbnail: {
            width: 35,
            srcFile: file,
            name: this.localFile,
            bucketPath: `${this._sitePath}/${this.thumbnails}`,
          },
        });
        this.$store.dispatch('pushDropletVal', {
          array: this.activeItem[this.type],
          val: {
            _id: this.guid(),
            src: filePath,
            altText: path.basename(filePath),
            subTitle: false,
          },
        });
        this.destroyCropper();
        this.targetFile = null;
        this.targetName = '';
      } catch (err) {
        if (!err.message.match(/aborted/gi)) {
          this.$log.error(err);
          await this._reportError({
            error: err,
            fn: 'item uploadCropped',
          });
        }
      }
    },
    resetCropper() {
      this.cropper.reset();
    },
    rotateImage(direction) {
      const deg = direction === 'clockwise' ? 90 : -90;
      this.cropper.rotate(deg);
    },
    async uploadGallery(e) {
      this.$validator.validateAll().then(async (result) => {
        // run only if file types and dimensions are correct
        if (result) {
          this.loading = true;
          this.showAll = false;
          const newFiles = [...e.target.files];
          if (newFiles.length > 0) {
            try {
              // check for expired sessions first
              await this._cognitoCheckSession();
              await Promise.all(newFiles.map(async (f) => {
                let fileName = path.basename(f.name);
                fileName = `${this.prefix}${_.trim(fileName)}`;
                fileName = _.replace(fileName, /\s+/g, '_');
                const filePath = `/${this.bucketPath}${fileName}`;
                await this.uploadFile({
                  file: `${this._sitePath}${filePath}`,
                  body: f,
                  thumbnail: {
                    width: 35,
                    srcFile: f,
                    name: fileName,
                    bucketPath: `${this._sitePath}/${this.thumbnails}`,
                  },
                });
                this.$store.dispatch('pushDropletVal', {
                  array: this.activeItem[this.type],
                  val: {
                    _id: this.guid(),
                    src: filePath,
                    altText: path.basename(filePath),
                    subTitle: false,
                  },
                });
              }));
            } catch (err) {
              if (!err.message.match(/aborted/gi)) {
                this.$log.error(err);
                await this._reportError({
                  error: err,
                  fn: 'uploadGallery',
                });
              }
            } finally {
              this.loading = false;
            }
          }
          // reset value to null so we can get the "change" event to run
          // in any case. Even when the user selects the same file
          e.target.value = null;
        }
      });
    },
    getImg(image) {
      return path.basename(image.src);
    },
    findIndex(id) {
      return _.findIndex(this.activeItem[this.type], item => item._id === id);
    },
    confirmDelete(image) {
      this.activeDelete = image;
      this.confirmDeleteImg = true;
    },
    async removeImg() {
      if (this.$refs.paginator) {
        this.$refs.paginator.goToPage(1);
      }
      // send off to be deleted from s3 if not default images
      this.showAll = false;
      this.loading = true;
      this.deleting = true;
      this.confirmDeleteImg = false;
      // prep notifcation type
      const notif = {
        id: this.guid(),
      };
      const image = this.activeDelete;
      const file = path.basename(image.src);
      const index = this.findIndex(image._id);
      // const thumb = this._getThumbFromFile(image.src);
      try {
        // we don't want to delete these currently as there can be
        // different archived and published states of dynamic droplets
        // delete original from s3
        // await this._s3deleteFile({
        //   key: `${this._fullSiteFolder}/${this.bucketPath}${file}`,
        // });
        // delete thumbnail from s3
        // await this._s3deleteFile({
        //   key: `${this._fullSiteFolder}/${this.thumbnails}${thumb}`,
        // });
        // remove from list
        this.$store.dispatch('spliceDropletVal', {
          array: this.activeItem[this.type],
          index,
        });
        await this._SEND([{
          name: 'updateSiteAttr',
          payload: {
            siteId: this.currentSite._id,
            attr: 'dynamic',
            value: this.currentSite.dynamic,
          },
        }]);
        // set for notifications
        notif.type = 'success';
        notif.message = `Successfully deleted image: ${file}`;
      } catch (err) {
        this.$log.error('error: ', err);
        // set for notifications
        notif.type = 'error';
        notif.message = `${err.message || err}. This has been reported.`;
        await this._reportError({
          error: err,
          fn: `Remove gallery img | dynamic | ${this.activeSiteType} `,
        });
      } finally {
        // send off to notifications
        this.$store.dispatch('addNotification', notif);
        // kill loader
        this.loading = false;
        this.deleting = false;
      }
    },
    setImageValue(e, image, key) {
      this.$store.dispatch('setDropletVal', {
        drp: image,
        key,
        val: this._getInputVal(e.target.value),
      });
    },
    getImgName(name) {
      return path.basename(name);
    },
    formatErrors(ers) {
      return ers.join(' ');
    },
    startCase(text) {
      return _.startCase(text);
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/top-control-bar/dynamic-data/edit-item/inputs/gallery/index.js